import * as React from 'react';
import * as A from 'fp-ts/Array';
import * as E from 'fp-ts/Either';
import * as t from 'io-ts';
import { constFalse, pipe } from 'fp-ts/lib/function';
import parseISO from 'date-fns/parseISO';
import isToday from 'date-fns/isToday';
import {
  FullMatchFragment,
  useMatchVoteWinnersQuery,
} from '../../../generated/graphql';

import { Card } from '../../../components/Card';
import { TextButton, TextLink } from '../../../components/ClickableText';
import { paths } from '../../../paths';
import { CardHeading } from '../../../components/CardHeading';
import { isNil } from '../../../utils/isNil';
import { VoteWinners } from '../../../components/VoteWinners';
import { useStore } from '../../../contexts/StoreContext';
import { Button, ButtonLink } from '../../../components/Button';

interface Props {
  match: Pick<FullMatchFragment, 'id' | 'playedAt'>;
  isTeamMember: boolean;
  isSignedIn: boolean;
}

const VoteWinnersBlurOverlay: React.FC<{
  matchId: string;
  isTeamMember: boolean;
}> = ({ matchId, isTeamMember }) => {
  const store = useStore();
  const storageKey = `mh.${matchId}.vwr`;

  const [revealed, setRevealed] = React.useState(() =>
    pipe(store.get(storageKey), t.boolean.decode, E.getOrElse(constFalse)),
  );

  const revealWinners = () => {
    store.set(storageKey, true);
    setRevealed(true);
  };

  if (revealed) {
    return null;
  }

  return (
    <div className="bg-grey-800 absolute inset-0 backdrop-filter backdrop-blur bg-opacity-40 z-10 p-4 flex flex-col">
      <CardHeading className="text-white">Vote Winners</CardHeading>
      <div className="flex items-center justify-center my-auto">
        <div className="max-w-xs space-y-4 text-center">
          {isTeamMember ? (
            <ButtonLink to={paths.match.votes.read(matchId)}>
              Read votes
            </ButtonLink>
          ) : null}

          {isTeamMember ? (
            <TextButton onClick={revealWinners}>Reveal winners</TextButton>
          ) : (
            <Button onClick={revealWinners}>Reveal winners</Button>
          )}
        </div>
      </div>
    </div>
  );
};

export const VoteWinnersCard: React.FC<Props> = ({
  match,
  isTeamMember,
  isSignedIn,
}) => {
  const [matchVoteWinnersResponse] = useMatchVoteWinnersQuery({
    variables: { matchId: match.id },
  });

  const maybeWinners =
    matchVoteWinnersResponse.data?.match?.voteResult?.winners;

  if (isNil(maybeWinners) || !A.isNonEmpty(maybeWinners)) {
    return <p>'boroken'</p>;
  }

  const isMatchToday = pipe(match.playedAt, parseISO, isToday);

  return (
    <Card className="bg-white relative overflow-hidden change-transform">
      <div className="space-y-4">
        <CardHeading>Vote Winners</CardHeading>
        <VoteWinners winners={maybeWinners} isSignedIn={isSignedIn} />
        <p className="text-sm">
          {isTeamMember ? (
            <TextLink to={paths.match.votes.read(match.id)}>
              Read votes
            </TextLink>
          ) : (
            <TextLink to={paths.match.votes.summary(match.id)}>
              View summary
            </TextLink>
          )}
        </p>
      </div>
      {isMatchToday ? (
        <VoteWinnersBlurOverlay
          matchId={match.id}
          isTeamMember={isTeamMember}
        />
      ) : null}
    </Card>
  );
};
